import { useQuery } from "@tanstack/react-query";
import { useWalletClient } from "wagmi";
import { fetchTokenBalances } from "@/services/real/balance.service";
import { QUERY_KEYS } from "@/config";
import { type TokenId, TOKEN_IDS } from "@/types";
import { useWalletConnection } from "../wallet";

type TokenBalances = Partial<Record<TokenId, WalletBalance>>;

export interface WalletBalance {
    tokenId: TokenId;
    tokenBalance: string;
}

type UseWalletBalancesReturn = {
    tokenBalances: TokenBalances;
    isLoading: boolean;
    isError: boolean;
    error: Error | null;
    isSuccess: boolean;
    isPending: boolean;
    getWalletTokenBalance: (tokenId: TokenId) => WalletBalance;
};

export const useWalletBalances = (): UseWalletBalancesReturn => {
    const { address } = useWalletConnection();
    return useAddressBalances(address);
};

export const useAddressBalances = (address: string): UseWalletBalancesReturn => {
    const { data: walletClient } = useWalletClient();
    const {
        data: balances,
        isLoading,
        isError,
        error,
        isSuccess,
        isPending,
    } = useQuery({
        queryKey: QUERY_KEYS.TOKENS.BALANCE.LIST(address),
        queryFn: async () => {
            if (!address) {
                throw new Error("Wallet not connected");
            }
            return await fetchTokenBalances(address as `0x${string}`, walletClient).then((balances) => {
                // @TODO enable posthog tracking later
                // posthog.setPersonProperties({
                //     address,
                //     balances: balances.reduce(
                //         (acc, balance) => {
                //             acc[`balance_${balance.tokenId}`] = +balance.tokenBalance;
                //             return acc;
                //         },
                //         {} as Record<string, number>,
                //     ),
                // });
                return balances;
            });
        },
        enabled: !!address,
    });

    const emptyBalances = Object.values(TOKEN_IDS).reduce((acc, tokenId) => {
        acc[tokenId] = {
            tokenId,
            tokenBalance: "0.00",
        };
        return acc;
    }, {} as TokenBalances);

    const tokenBalances = balances
        ? balances.reduce((acc, balance) => {
              acc[balance.tokenId] = {
                  tokenId: balance.tokenId,
                  tokenBalance: balance.tokenBalance,
              };
              return acc;
          }, emptyBalances)
        : emptyBalances;

    const getWalletTokenBalance = (tokenId: TokenId): WalletBalance => {
        return tokenBalances[tokenId] || { tokenId, tokenBalance: "0.00" };
    };

    return { getWalletTokenBalance, tokenBalances, isLoading, isError, error, isSuccess, isPending };
};
